/* assets/pl-media-groups.js
   Lazy media groups for the PDP gallery.

   snippets/product-media.liquid (lazy_media_groups: true) server-renders only the
   selected color's media group plus ungrouped media. When the shopper switches
   color, Focal's ProductMedia._onVariantChanged (wrapped by an inline hook in product-media.liquid) awaits
   PLMediaGroups.ensure() BEFORE it filters, so the new color's items are in the
   DOM when Focal toggles .is-filtered and reloads Flickity. Everything after
   that (filtering, Flickity reload, ordering by data-original-position) is
   Focal's existing code path.

   Items for a group come from the Section Rendering API:
     /products/{handle}?variant={id}&section_id={main section id}
   Responses are cached per group and prefetched on swatch hover / touch / focus,
   so a switch usually costs no network wait. Groups that were already inserted
   stay in the DOM (filtered) so switching back is instant.

   ensure() resolves:
     true  - group present (or nothing to do); Focal continues.
     false - fetch failed, or a newer color change superseded this one; Focal
             returns early and the gallery keeps its current state instead of
             being filtered to nothing.
*/
(function () {
  'use strict';
  if (window.PLMediaGroups) return;

  var responseCache = {};   // groupKey -> Promise<Document|null>
  var productJson = null;   // Promise<product.js>
  var requestSeq = 0;

  function normalize(value) {
    return String(value == null ? '' : value).toLowerCase().split(' ').join('-');
  }

  function rootUrl() {
    var r = window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.rootUrlWithoutSlash;
    return r || '';
  }

  function loadedKeys(pm) {
    if (!pm._plLoadedGroups) {
      pm._plLoadedGroups = new Set([pm.getAttribute('data-group-key') || '']);
    }
    return pm._plLoadedGroups;
  }

  function groupKeyForVariant(pm, variant) {
    var pos = parseInt(pm.getAttribute('data-group-position'), 10) || 0;
    if (!pos || !variant) return null;
    var options = variant.options || [variant.option1, variant.option2, variant.option3];
    return normalize(options[pos - 1]);
  }

  function sectionUrl(pm, variantId) {
    var handle = pm.getAttribute('product-handle');
    var sectionId = pm.getAttribute('data-section-id');
    return rootUrl() + '/products/' + encodeURIComponent(handle) + '?variant=' + encodeURIComponent(variantId) + '&section_id=' + encodeURIComponent(sectionId);
  }

  function fetchGroup(pm, key, variantId) {
    if (responseCache[key]) return responseCache[key];
    responseCache[key] = fetch(sectionUrl(pm, variantId), { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html');
      })
      .catch(function (err) {
        delete responseCache[key]; // allow a retry on the next attempt
        if (window.console) console.warn('[pl-media-groups] fetch failed', err);
        return null;
      });
    return responseCache[key];
  }

  // Insert fetched nodes that are not already present, keeping DOM order by
  // data-original-position. Inserted nodes start .is-filtered; Focal un-filters
  // the ones that belong to the new variant a moment later.
  function mergeInto(container, fetchedNodes, existingSelector) {
    if (!container || !fetchedNodes.length) return 0;
    var existing = {};
    container.querySelectorAll(existingSelector).forEach(function (n) {
      existing[n.getAttribute('data-media-id')] = true;
    });
    var added = 0;
    fetchedNodes.forEach(function (node) {
      var id = node.getAttribute('data-media-id');
      if (!id || existing[id]) return;
      var clone = document.importNode(node, true);
      clone.classList.add('is-filtered');
      clone.classList.remove('is-selected', 'is-initial-selected');
      clone.removeAttribute('aria-current');
      var pos = parseInt(clone.getAttribute('data-original-position'), 10);
      var siblings = Array.prototype.slice.call(container.querySelectorAll(':scope > [data-media-id]'));
      var before = null;
      for (var i = 0; i < siblings.length; i++) {
        var sp = parseInt(siblings[i].getAttribute('data-original-position'), 10);
        if (!isNaN(pos) && !isNaN(sp) && sp > pos) { before = siblings[i]; break; }
      }
      container.insertBefore(clone, before);
      existing[id] = true;
      added++;
    });
    return added;
  }

  function carouselContainer(pm) {
    var fc = pm.querySelector('flickity-carousel');
    if (!fc) return null;
    // While Flickity is active, cells live in .flickity-slider. Focal's reload()
    // destroys Flickity (moving cells back to the element) and re-sorts all
    // children by data-original-position, so appending to the element is safe
    // for a node that starts hidden (.is-filtered is display:none).
    return fc;
  }

  function insertGroup(pm, doc) {
    var sectionId = pm.getAttribute('data-section-id');
    var fetchedPm = doc.getElementById('carousel-gallery-' + sectionId);
    if (!fetchedPm) return false;

    // Carousel cells. Existing cells may be inside .flickity-slider, so check
    // presence across the whole carousel, then insert into the element itself.
    var fc = carouselContainer(pm);
    if (fc) {
      var present = {};
      fc.querySelectorAll('.product__media-item[data-media-id]').forEach(function (n) { present[n.getAttribute('data-media-id')] = true; });
      var fetchedCells = Array.prototype.slice.call(fetchedPm.querySelectorAll('flickity-carousel .product__media-item[data-media-id]'))
        .filter(function (n) { return !present[n.getAttribute('data-media-id')]; });
      fetchedCells.forEach(function (node) {
        var clone = document.importNode(node, true);
        clone.classList.add('is-filtered');
        clone.classList.remove('is-selected', 'is-initial-selected');
        fc.appendChild(clone);
      });
    }

    // Thumbnails and dots.
    mergeInto(pm.querySelector('.product__thumbnail-list-inner'),
      Array.prototype.slice.call(fetchedPm.querySelectorAll('.product__thumbnail-list-inner > .product__thumbnail-item')),
      ':scope > .product__thumbnail-item');
    mergeInto(pm.querySelector('.dots-nav'),
      Array.prototype.slice.call(fetchedPm.querySelectorAll('.dots-nav > .dots-nav__item')),
      ':scope > .dots-nav__item');

    // Desktop grid (sibling of product-media, only when show_desktop_gallery).
    var grid = document.getElementById('desktop-gallery-' + sectionId);
    var fetchedGrid = doc.getElementById('desktop-gallery-' + sectionId);
    if (grid && fetchedGrid) {
      mergeInto(grid,
        Array.prototype.slice.call(fetchedGrid.querySelectorAll(':scope > .product__media-item')),
        ':scope > .product__media-item');
    }
    return true;
  }

  function notifyUpdated() {
    // Focal toggles .is-filtered and calls mainCarousel.reload() right after
    // ensure() resolves; pl-product-media.js re-initializes on this event once
    // the new Flickity instance exists.
    setTimeout(function () {
      document.dispatchEvent(new CustomEvent('pl:media-groups-updated'));
    }, 60);
  }

  function ensure(pm, variant) {
    var seq = ++requestSeq;
    var key = groupKeyForVariant(pm, variant);
    if (key === null) return Promise.resolve(true);
    var keys = loadedKeys(pm);
    if (keys.has(key)) {
      notifyUpdated();
      return Promise.resolve(true);
    }
    pm.setAttribute('aria-busy', 'true');
    return fetchGroup(pm, key, variant.id).then(function (doc) {
      pm.removeAttribute('aria-busy');
      if (seq !== requestSeq) return false; // superseded by a newer change
      if (!doc) return false;
      if (!keys.has(key)) {
        if (!insertGroup(pm, doc)) return false;
        keys.add(key);
      }
      notifyUpdated();
      return true;
    });
  }

  // ---------- Prefetch on intent (hover, touch, keyboard focus) ----------
  function loadProductJson(pm) {
    if (!productJson) {
      productJson = fetch(rootUrl() + '/products/' + encodeURIComponent(pm.getAttribute('product-handle')) + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return productJson;
  }

  function prefetchValue(pm, value) {
    var key = normalize(value);
    if (loadedKeys(pm).has(key) || responseCache[key]) return;
    var pos = parseInt(pm.getAttribute('data-group-position'), 10) || 0;
    loadProductJson(pm).then(function (product) {
      if (!product || !product.variants) return;
      var match = null;
      for (var i = 0; i < product.variants.length; i++) {
        var v = product.variants[i];
        if (normalize(v.options[pos - 1]) === key) {
          if (!match || (v.available && !match.available)) match = v;
          if (match.available) break;
        }
      }
      if (match) fetchGroup(pm, key, match.id);
    });
  }

  function onIntent(event) {
    var pm = document.querySelector('product-media[data-lazy-groups]');
    if (!pm) return;
    var pos = pm.getAttribute('data-group-position');
    var formId = pm.getAttribute('form-id');
    var target = event.target && event.target.closest ? event.target : null;
    if (!target) return;
    var input = null;
    var label = target.closest('label[for]');
    if (label) input = document.getElementById(label.getAttribute('for'));
    if (!input && target.matches('input[type="radio"]')) input = target;
    if (input && input.name === 'option' + pos && (input.getAttribute('form') === formId || (input.form && input.form.id === formId))) {
      prefetchValue(pm, input.value);
      return;
    }
    var ddItem = target.closest('.color-dropdown__item');
    if (ddItem && ddItem.value) prefetchValue(pm, ddItem.value);
  }

  document.addEventListener('pointerover', onIntent, { passive: true });
  document.addEventListener('touchstart', onIntent, { passive: true, capture: true });
  document.addEventListener('focusin', onIntent);

  window.PLMediaGroups = { ensure: ensure, prefetch: prefetchValue };
})();
